import { useEffect, useState } from "react";
import { Download, FileText, Receipt } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine, Cell } from "recharts";
import { api, errMsg } from "../lib/api";
import { money, monthLabel, downloadCSV } from "../lib/format";
import { Panel, Empty } from "./Widgets";
import { Button } from "./ui/button";
import { BrandBadge } from "./Brands";
import { CompanyOptions } from "./Companies";

const sel = "h-8 rounded-md bg-[#0B0F17] border border-slate-700 text-xs px-2";
const BAR = { Crumble: "#F59E0B", Zappay: "#3B82F6", Untagged: "#64748B" };
const Stat = ({ label, value, id }) => (
  <div className="rounded border border-slate-800 bg-[#0B0F17] p-3"><div className="text-[11px] font-mono uppercase text-slate-500">{label}</div>
    <div className="font-mono text-lg text-slate-100" data-testid={id}>{value}</div></div>
);

function SaasChart({ months, targets }) {
  const keys = ["Crumble", "Zappay", ...(months.some((m) => m.Untagged) ? ["Untagged"] : [])];
  return (
    <div className="h-[240px]" data-testid="saas-chart">
      <ResponsiveContainer>
        <BarChart data={months.map((m) => ({ ...m, label: monthLabel(m.month) }))} margin={{ left: 10, right: 10, top: 10 }} maxBarSize={48}>
          <CartesianGrid stroke="#1E293B" vertical={false} />
          <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
          <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`} />
          <Tooltip cursor={{ fill: "#1E293B" }} labelStyle={{ color: "#F1F5F9" }} itemStyle={{ color: "#E2E8F0" }}
            contentStyle={{ background: "#0B0F17", border: "1px solid #334155", borderRadius: 6 }} formatter={(v) => money(v)} />
          <Legend content={() => (
            <div className="flex justify-center gap-4 text-[11px]">{keys.map((k) => <span key={k} className="flex items-center gap-1" style={{ color: BAR[k] }}>
              <span className="inline-block w-2.5 h-2.5" style={{ background: BAR[k] }} />{k}</span>)}</div>)} />
          {keys.map((k) => <Bar key={k} dataKey={k} fill={BAR[k]} radius={[3, 3, 0, 0]} />)}
          {["Crumble", "Zappay"].filter((b) => targets?.[b] > 0).map((b) => (
            <ReferenceLine key={`t-${b}`} y={targets[b]} stroke={BAR[b]} strokeDasharray="4 4" label={{ value: `${b} target`, fill: BAR[b], fontSize: 10, position: "insideTopRight" }} />))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function useTargets() {
  const [t, setT] = useState({ Crumble: 0, Zappay: 0 });
  useEffect(() => { api.get("/saas-targets").then((r) => setT(r.data)).catch(() => {}); }, []);
  return { ...t, set: setT };
}

function TargetProgress({ month, targets, testPrefix = "saas-target" }) {
  const set = ["Crumble", "Zappay"].filter((b) => targets[b] > 0);
  if (!month || !set.length) return null;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid={`${testPrefix}-progress`}>
      {set.map((b) => {
        const v = month[b] || 0, p = Math.round((v / targets[b]) * 100);
        return (
          <div key={b} className="rounded border border-slate-800 bg-[#0B0F17] p-3" data-testid={`${testPrefix}-${b}`}>
            <div className="flex justify-between text-[11px] font-mono uppercase text-slate-500">
              <span>{b} target · {monthLabel(month.month)}</span><span style={{ color: BAR[b] }} data-testid={`${testPrefix}-${b}-pct`}>{p}%</span></div>
            <div className="mt-2 h-2 rounded bg-slate-800 overflow-hidden">
              <div className="h-full rounded transition-[width] duration-700" style={{ width: `${Math.min(p, 100)}%`, background: BAR[b] }} /></div>
            <div className="mt-1 text-xs font-mono text-slate-400">{money(v)} of {money(targets[b])}{v >= targets[b] ? " · reached" : ` · ${money(targets[b] - v)} to go`}</div>
          </div>);
      })}
    </div>
  );
}

function TargetEditor({ targets, onSaved }) {
  const [v, setV] = useState(null);
  const save = async () => {
    try {
      const { data } = await api.put("/saas-targets", { Crumble: Number(v.Crumble) || 0, Zappay: Number(v.Zappay) || 0 });
      onSaved(data); setV(null); toast.success("Monthly targets saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!v) return <button data-testid="saas-target-edit" onClick={() => setV({ Crumble: targets.Crumble, Zappay: targets.Zappay })}
    className="text-xs text-blue-400 hover:text-blue-300">{targets.Crumble || targets.Zappay ? "Edit monthly targets" : "Set monthly SaaS targets"}</button>;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="saas-target-form">
      {["Crumble", "Zappay"].map((b) => <label key={b} className="text-xs text-slate-400 flex items-center gap-1">{b} $
        <input type="number" min="0" data-testid={`saas-target-${b}-input`} value={v[b]} onChange={(e) => setV({ ...v, [b]: e.target.value })} className={`${sel} w-28`} /> / mo</label>)}
      <Button data-testid="saas-target-save" size="sm" onClick={save} className="bg-blue-500 hover:bg-blue-600 text-white">Save</Button>
      <Button data-testid="saas-target-cancel" size="sm" variant="outline" onClick={() => setV(null)}>Cancel</Button>
    </div>
  );
}

function Filters({ f, set, months, agents }) {
  const ch = (k) => (e) => set({ ...f, [k]: e.target.value });
  return (
    <div className="flex flex-wrap gap-2 p-4 border-b border-slate-800 bg-[#0d1320]">
      <select data-testid="saas-from" value={f.from_month} onChange={ch("from_month")} className={sel}>{months.map((m) => <option key={m} value={m}>From {monthLabel(m)}</option>)}</select>
      <select data-testid="saas-to" value={f.to_month} onChange={ch("to_month")} className={sel}>{months.map((m) => <option key={m} value={m}>To {monthLabel(m)}</option>)}</select>
      <select data-testid="saas-brand" value={f.brand} onChange={ch("brand")} className={sel}>
        <option value="">Crumble + Zappay</option><option value="Crumble">Crumble only</option><option value="Zappay">Zappay only</option><option value="none">Untagged only</option>
      </select>
      <select data-testid="saas-company" value={f.company} onChange={ch("company")} className={sel}>
        <CompanyOptions />
      </select>
      <select data-testid="saas-agent" value={f.agent_id} onChange={ch("agent_id")} className={sel}>
        <option value="">All agents</option>{agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
    </div>
  );
}

const Row3 = ({ r }) => <><td className="text-right font-mono text-amber-300">{money(r.Crumble)}</td><td className="text-right font-mono text-blue-300">{money(r.Zappay)}</td>
  <td className="text-right font-mono text-slate-500">{money(r.Untagged)}</td><td className="text-right font-mono text-slate-100">{money(r.total)}</td></>;
const Head3 = () => <><th className="!text-right">Crumble</th><th className="!text-right">Zappay</th><th className="!text-right">Untagged</th><th className="!text-right">Total</th></>;

function SaasTables({ d }) {
  return (
    <>
      <table className="tbl w-full" data-testid="saas-months-table">
        <thead><tr><th>Month</th><th className="!text-right">Merchants</th><Head3 /></tr></thead>
        <tbody>{d.months.map((m) => <tr key={m.month}><td className="font-mono">{monthLabel(m.month)}</td><td className="text-right font-mono">{m.merchants}</td><Row3 r={m} /></tr>)}</tbody>
      </table>
      <table className="tbl w-full" data-testid="saas-agents-table">
        <thead><tr><th>Agent</th><th className="!text-right">Merchants</th><Head3 /></tr></thead>
        <tbody>{d.agents.map((a) => <tr key={a.owner} data-testid={`saas-agent-${a.agent_id || "unassigned"}`}><td>{a.owner}</td><td className="text-right font-mono">{a.merchants}</td><Row3 r={a} /></tr>)}</tbody>
      </table>
      <details>
        <summary className="cursor-pointer text-xs text-slate-400" data-testid="saas-merchants-toggle">{d.merchants.length} merchants paying a fee</summary>
        <table className="tbl w-full mt-2" data-testid="saas-merchants-table">
          <thead><tr><th>Merchant</th><th>Brand</th><th>Company</th><th>Agent</th><th className="!text-right">Fee rate</th><th className="!text-right">Months</th><th className="!text-right">Fee revenue</th></tr></thead>
          <tbody>{d.merchants.map((m) => <tr key={m.merchant_id} data-testid={`saas-merchant-${m.merchant_id}`}><td>{m.merchant_name || "—"}<div className="font-mono text-[11px] text-slate-500">{m.merchant_id}</div></td>
            <td><BrandBadge brand={m.brand} /></td><td className="text-slate-400">{m.company}</td><td className="text-slate-300">{m.owner}</td>
            <td className="text-right font-mono">{money(m.fee_rate)}</td><td className="text-right font-mono">{m.months}</td><td className="text-right font-mono text-emerald-400">{money(m.total)}</td></tr>)}</tbody>
        </table>
      </details>
    </>
  );
}

export function SaasFeeReport({ company = "" }) {
  const [agents, setAgents] = useState([]);
  const [f, setF] = useState(null);
  const [d, setD] = useState(null);
  const targets = useTargets();
  const setTargets = targets.set;
  useEffect(() => {
    api.get("/agents").then((r) => setAgents(r.data));
    api.get("/saas-fees").then((r) => {
      const ms = r.data.available_months;
      if (ms.length) setF({ from_month: ms[0], to_month: ms[ms.length - 1], brand: "", company, agent_id: "" }); else setD(r.data);
    }).catch((e) => toast.error(errMsg(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setF((x) => (x ? { ...x, company } : x)); }, [company]);
  const params = f ? Object.fromEntries(Object.entries(f).filter(([, v]) => v)) : null;
  useEffect(() => {
    if (!params) return undefined;
    let live = true;
    api.get("/saas-fees", { params }).then((r) => live && setD(r.data)).catch((e) => live && toast.error(errMsg(e)));
    return () => { live = false; };
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  const name = f ? `saas-fees-${f.from_month}-to-${f.to_month}` : "saas-fees";
  const csv = () => downloadCSV(`${name}.csv`, ["Merchant", "Merchant ID", "Brand", "Company", "Agent", "Fee rate", "Months", "Fee revenue"],
    d.merchants.map((m) => [m.merchant_name, m.merchant_id, m.brand || "", m.company, m.owner, m.fee_rate, m.months, m.total]));
  const pdf = async () => {
    try {
      const r = await api.get("/saas-fees/pdf", { params, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${name}.pdf`; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!f) return <Panel title="Monthly SaaS fee revenue"><Empty>{d ? "No reports uploaded yet." : "Loading…"}</Empty></Panel>;
  return (
    <Panel title="Monthly SaaS fee revenue · full fee rate" action={d && (
      <div className="flex gap-2">
        <Button data-testid="saas-csv" size="sm" variant="outline" onClick={csv}><Download size={13} className="mr-1" />CSV</Button>
        <Button data-testid="saas-pdf" size="sm" variant="outline" onClick={pdf}><FileText size={13} className="mr-1" />PDF</Button>
      </div>)}>
      <Filters f={f} set={setF} months={d?.available_months || [f.to_month]} agents={agents} />
      {!d ? <Empty>Loading…</Empty> : (
        <div className="p-4 space-y-5" data-testid="saas-report">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total SaaS fees" value={money(d.totals.total)} id="saas-total" />
            <Stat label="Crumble" value={money(d.totals.Crumble)} id="saas-crumble" />
            <Stat label="Zappay" value={money(d.totals.Zappay)} id="saas-zappay" />
            <Stat label="Merchants paying" value={d.totals.merchants} id="saas-merchants" />
          </div>
          {d.totals.Untagged > 0 && <div className="text-xs text-amber-300" data-testid="saas-untagged-note">{money(d.totals.Untagged)} is from merchants not yet tagged Crumble or Zappay.</div>}
          {d.months.length === 0 ? <Empty>No monthly fees in this period.</Empty> : <>
            <TargetProgress month={d.months[d.months.length - 1]} targets={targets} />
            <SaasChart months={d.months} targets={targets} /><SaasTables d={d} /></>}
          <SaasForecast brand={f.brand} company={f.company} agentId={f.agent_id} />
          <TargetEditor targets={targets} onSaved={setTargets} />
        </div>
      )}
    </Panel>
  );
}

export function SaasForecast({ brand = "", company = "", agentId = "", compact = false }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    const p = Object.fromEntries(Object.entries({ brand, company, agent_id: agentId }).filter(([, v]) => v));
    let live = true;
    api.get("/saas-forecast", { params: p }).then((r) => live && setD(r.data)).catch(() => live && setD(null));
    return () => { live = false; };
  }, [brand, company, agentId]);
  if (!d?.base_month) return null;
  const keys = d.brands.filter((k) => k !== "Untagged" || d.months.some((m) => m.Untagged) || d.brands.length === 1);
  const data = [...d.history.map((h) => ({ ...h, kind: "Actual" })), ...d.months.map((m) => ({ ...m, kind: "Forecast" }))]
    .map((r) => ({ ...r, label: `${monthLabel(r.month)}${r.kind === "Forecast" ? " *" : ""}` }));
  return (
    <div className="rounded border border-slate-800 bg-[#0B0F17] p-3 space-y-3" data-testid={compact ? "dashboard-saas-forecast" : "saas-forecast"}>
      <div className="text-[11px] font-mono uppercase text-slate-500">Forecast · next 3 months after {monthLabel(d.base_month)} · scheduled fees + recent merchant trend</div>
      <div className="grid grid-cols-3 gap-3">
        {d.months.map((m) => (
          <div key={m.month} data-testid={`saas-forecast-${m.month}`}>
            <div className="text-xs text-slate-400">{monthLabel(m.month)}</div>
            <div className="font-mono text-lg text-slate-100" data-testid={`saas-forecast-total-${m.month}`}>{money(m.total)}</div>
            <div className="text-[11px] font-mono text-slate-500">{keys.filter((k) => k !== "Untagged").map((k) => <span key={k} className="mr-2" style={{ color: BAR[k] }}>{k} {money(m[k])}</span>)}</div>
          </div>))}
      </div>
      {!compact && <>
        <div className="h-[220px]" data-testid="saas-forecast-chart">
          <ResponsiveContainer>
            <BarChart data={data} margin={{ left: 10, right: 10, top: 10 }} maxBarSize={40}>
              <CartesianGrid stroke="#1E293B" vertical={false} />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`} />
              <Tooltip cursor={{ fill: "#1E293B" }} labelStyle={{ color: "#F1F5F9" }} itemStyle={{ color: "#E2E8F0" }}
                contentStyle={{ background: "#0B0F17", border: "1px solid #334155", borderRadius: 6 }} formatter={(v) => money(v)} />
              {keys.map((k) => <Bar key={k} dataKey={k} stackId="f" fill={BAR[k]}>
                {data.map((r) => <Cell key={r.month} fillOpacity={r.kind === "Forecast" ? 0.45 : 1} />)}</Bar>)}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <table className="tbl w-full" data-testid="saas-forecast-table">
          <thead><tr><th>Month</th>{keys.map((k) => <th key={k} className="!text-right">{k}</th>)}<th className="!text-right">Total</th><th className="!text-right">Of which scheduled fees</th></tr></thead>
          <tbody>{d.months.map((m) => <tr key={m.month}><td className="font-mono">{monthLabel(m.month)} <span className="text-[10px] text-slate-500">forecast</span></td>
            {keys.map((k) => <td key={k} className="text-right font-mono" title={`~${m[`${k}_merchants`]} merchants`}>{money(m[k])}</td>)}
            <td className="text-right font-mono text-slate-100">{money(m.total)}</td>
            <td className="text-right font-mono text-slate-500">{money(keys.reduce((s, k) => s + m[`${k}_scheduled`], 0))}</td></tr>)}</tbody>
        </table>
        <div className="text-[11px] text-slate-500" data-testid="saas-forecast-trend">
          {keys.map((k) => `${k}: ${d.trend[k].merchants} paying now, ${d.trend[k].avg_change >= 0 ? "+" : ""}${d.trend[k].avg_change} merchants/mo at avg ${money(d.trend[k].avg_fee)}`).join(" · ")}. * = forecast.
        </div>
      </>}
    </div>
  );
}

export function SaasFeeCard({ className = "", company = "", month = "" }) {
  const [d, setD] = useState(null);
  const targets = useTargets();
  useEffect(() => {
    const p = { ...(company ? { company } : {}), ...(month ? { from_month: month, to_month: month } : {}) };
    let live = true;
    api.get("/saas-fees", { params: p }).then((r) => live && setD(r.data)).catch(() => live && setD(null));
    return () => { live = false; };
  }, [company, month]);
  if (!d?.to_month) return null;
  return (
    <Panel title={`Monthly SaaS fees · ${monthLabel(d.to_month)}`} className={className} action={<Receipt size={16} className="text-emerald-400" />}>
      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-saas-card">
        <Stat label="Combined" value={money(d.totals.total)} id="dashboard-saas-total" />
        <Stat label="Crumble" value={money(d.totals.Crumble)} id="dashboard-saas-crumble" />
        <Stat label="Zappay" value={money(d.totals.Zappay)} id="dashboard-saas-zappay" />
        <Stat label="Merchants paying" value={d.totals.merchants} id="dashboard-saas-merchants" />
      </div>
      <div className="px-4 pb-4 space-y-3"><TargetProgress month={d.months[d.months.length - 1]} targets={targets} testPrefix="dashboard-saas-target" />
        <SaasForecast company={company} compact /></div>
    </Panel>
  );
}
